import React from 'react';

export interface SuperscriptTextProps {
  /** Text content with HTML-like <sup>content</sup> tags for superscript rendering */
  text: string;
  /** Font size of superscript relative to parent (default: 0.65em) */
  supFontSize?: string;
  /** Vertical alignment of superscript (default: 0.3em) */
  supVerticalAlign?: string;
  /** Left margin of superscript (default: 0.1em) */
  supMarginLeft?: string;
}

/**
 * Renders text with superscript formatting.
 * Parses HTML-like <sup>content</sup> tags and renders them as superscript with customizable styling.
 * 
 * @example
 * <SuperscriptText text="FICO<sup>®</sup> Score" />
 * <SuperscriptText text="E=mc<sup>2</sup>" supFontSize="0.7em" />
 */
export function SuperscriptText({
  text,
  supFontSize = '0.65em',
  supVerticalAlign = '0.3em',
  supMarginLeft = '0.1em',
}: SuperscriptTextProps): React.ReactNode {
  const parts = text.split(/(<sup>.*?<\/sup>)/);

  return parts.map((part) => {
    if (part.startsWith('<sup>') && part.endsWith('</sup>')) {
      const content = part.slice(5, -6);
      return (
        <sup
          key={`sup-${content}`}
          style={{
            fontSize: supFontSize,
            verticalAlign: supVerticalAlign,
            marginLeft: supMarginLeft,
            lineHeight: '1',
          }}
        >
          {content}
        </sup>
      );
    }
    return <React.Fragment key={`text-${part}`}>{part}</React.Fragment>;
  });
}
